import React, { useCallback, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppContext } from '../../../App'
import AddAlertIcon from '../../../assets/icons/AddAlertIcon'
import { NonInteractiveTooltip, UltraSmallButton } from '../../custom'
import { setAlertModalData } from '../../../reducer/alerts.reducer'
import {
    buildAlertPatternFromSeriesGroup,
    findUserAlertForPatternId,
    getUserAlertMessageFromCondition,
    resolvePatternAlertModalPayload,
} from '../../../util/alerts'
import { UserAlert_Status } from '../../../proto/generated/nexus'
import { selectIndicators } from '../../../selectors/chart'
import { useUser } from '../../../context/UserContext'
import { FeatureCategory, FeatureSubCategory } from '../../adminUserRestrictions/Restrictions.constants'
import { FeatureCode } from '../../../util/RestrictionFeatureCode'
import { Group, setFeatureRestrictionDialog } from '../../../reducer/user.reducer'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import { RootState } from '../../../store'
import { Security } from '../../../proto/generated/security'

type PatternAlertButtonProps = {
    /** Chart series-group instance id — links one alert per pattern instance (like drawingId). */
    seriesGroupId: string
    /** Pattern indicator/script code, e.g. BULLISH_ENGULFING. */
    scriptCode: string
    inputValues: Record<string, string>
    chartId: string
    security?: Security
}

const PatternAlertButton: React.FC<PatternAlertButtonProps> = ({
    seriesGroupId,
    scriptCode,
    inputValues,
    chartId,
    security,
}) => {
    const dispatch = useDispatch()
    const appContext = useContext(AppContext)
    const indicators = useSelector(selectIndicators)
    const alerts = useSelector((state: RootState) => state.alertsStore.alerts)
    const { user: { hasAccess } } = useUser()
    const linkedAlert = findUserAlertForPatternId(alerts, seriesGroupId)
    const hasExistingAlert = Boolean(linkedAlert)
    const hasActiveAlert = linkedAlert?.status === UserAlert_Status.ACTIVE

    const handleClick = useCallback(async () => {
        if (!hasAccess(FeatureCategory.CHART, FeatureSubCategory.ALERTS)) {
            dispatch(setFeatureRestrictionDialog({
                open: true,
                feature: 'Alerts',
                group: Group.CHART,
                featureRestrictionCode: FeatureCode.CHART_ALERTS,
            }))
            return
        }

        const getSnapshot = appContext.getChartSnapshot?.[chartId]
        const snapshot = getSnapshot ? getSnapshot() : null
        if (!snapshot) return

        const primary = snapshot.securityList?.[0]
        const pattern = buildAlertPatternFromSeriesGroup(
            { id: seriesGroupId, scriptCode, inputValues },
            security ?? primary,
            indicators,
        )

        AnalyticsManager.logEvent(
            findUserAlertForPatternId(alerts, seriesGroupId) ? 'Edit Pattern Alert Clicked' : 'Add Pattern Alert Clicked',
            { source: 'series-meta', patternCode: scriptCode }
        )

        const { alert, edit, sourcePattern } = resolvePatternAlertModalPayload(alerts, pattern, snapshot)
        let message = alert.message
        if (!edit) {
            message = (await getUserAlertMessageFromCondition(
                alert.condition,
                primary?.code || '',
                indicators,
                {
                    chartSnapshot: snapshot,
                    chartTimeBucket: snapshot.timeBucket ?? alert.timeBucket,
                }
            )) || alert.message
        }

        dispatch(setAlertModalData({
            chartSnapshot: snapshot,
            alert: {
                ...alert,
                security: primary
                    ? { exchange: primary.exchange, code: primary.code }
                    : alert.security,
                message,
            },
            edit,
            sourcePattern,
        }))
    }, [alerts, appContext, chartId, seriesGroupId, scriptCode, inputValues, security, dispatch, hasAccess, indicators])

    return (
        <NonInteractiveTooltip
            placement="top"
            title={hasExistingAlert ? 'Edit alert on pattern' : 'Add alert on pattern'}
            arrow
        >
            <UltraSmallButton
                onClick={handleClick}
                color="inherit"
                size="small"
                sx={{
                    maxHeight: '24px',
                    borderRadius: '2px',
                    ...(hasActiveAlert ? { bgcolor: 'action.hover' } : {}),
                }}
            >
                <AddAlertIcon sx={{ fontSize: '18px', width: 18, height: 18 }} color="inherit" />
            </UltraSmallButton>
        </NonInteractiveTooltip>
    )
}

export default React.memo(PatternAlertButton)
